import CommonConstants from "@/utils/constants/CommonConstants.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import MetaData from "@/utils/common/MetaData.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ApprovalDecision from "@/utils/constants/ApprovalDecision.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import Translator from "@/utils/common/Translator.ts";
import {AxiosRequestConfig} from "axios";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {QuestionFieldsWithValues, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ApprovalCaseState from "@/utils/constants/ApprovalCaseState.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import NaMaMetaData = namaTypes.NaMaMetaData;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import DTOLargeData = namaTypes.DTOLargeData;
import ApproveRequest = namaTypes.ApproveRequest;
import EntityReferenceData = namaTypes.EntityReferenceData;
import DTOApprovalCase = namaTypes.DTOApprovalCase;
import DTOApprovalCaseStep = namaTypes.DTOApprovalCaseStep;

class QuestionFieldWithCriteria implements QuestionField {
  private _criteriaFn: () => string = () => "";

  setCriteriaCalculator(fn: () => string) {
    this._criteriaFn = fn;
  }

  get criteriaExpression(): string {
    return this._criteriaFn();
  }
}

export default class ApprovalUtils {
  private static createApprovalDialogAction(decisionId: string, approvalCase: namaTypes.DTOApprovalCase, fieldsMetadata: namaTypes.FieldMetaData[], presenter: EditPresenter | undefined): CommonDialogAction {
    return new CommonDialogAction(decisionId, async (object: Record<string, any>) => {
      let employee: EntityReferenceData | undefined = LoginUtils.loginInfo?.userInfo?.employee;
      if (!employee)
        return;
      object.decision = decisionId;
      let approveRequest = this.createApproveRequest(approvalCase, employee.id, object, fieldsMetadata);
      let settings: AxiosRequestConfig = {
        data: NamaCommon.stringify(approveRequest)
      };
      let response = await NamaCommon.namaFetch(NamaURLS.APPROVE, settings);
      if (response && NamaCommon.isSuccess(response)) {
        if (!response.data) {
          useMessagesStore().pushMessageStr(Translator.translateId("approvalRedirected"));
          return;
        }
        await NotificationUtils.updateApprovals();
        if (!presenter)
          return;
        if (ApprovalUtils.isApprovedToDeleteEntity(response.data)) {
          await presenter.onGoToListView();
          useMessagesStore().pushMessageStr(Translator.translateId("recordDeletedCorrectly"));
        } else
          await presenter.refresh(true, true)
      }
    });
  }

  private static createOtherDecisionDialogAction(decisionId: string, actions: CommonDialogAction[], entityType: string | undefined,
                                                 questionFields: QuestionField[], stepsAllowedForReturn: DTOApprovalCaseStep[]) {
    return new CommonDialogAction(decisionId, async (object: Record<string, any>) => {
      let otherDecisionPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter(entityType);
      otherDecisionPresenter.searchOwnerEntityType = DomainBaseEntities.ApprovalDefinition;
      await otherDecisionPresenter.edit(ApprovalUtils.addReturnToStepFieldAfterRemarks(questionFields, stepsAllowedForReturn), []);
      Object.assign(otherDecisionPresenter.record.value, object);
      let dialogActions = [...actions, CommonDialogAction.createCancelDialogAction(() => {
      })];
      let dialogBox = new DialogHandler(dialogActions, Translator.translateId(IdsAndConstants.OTHER_DECISION));
      await dialogBox.createPresenter(otherDecisionPresenter as WidgetsPresenter);
      await dialogBox.runDialogBox();
    });
  }

  static async showApprovalDialog(approvalCase: DTOApprovalCase, presenter?: EditPresenter) {
    if (!approvalCase || !approvalCase.approvedElement)
      return;
    let stepsAllowedForReturn: DTOApprovalCaseStep[] = ApprovalUtils.calcStepsAllowedForReturn(await ApprovalUtils.loadTakenStepsIfMissing(approvalCase));
    let fields: string | undefined = approvalCase.nextUpdatedFields;
    let fieldsMetadata: FieldMetaData[] = ApprovalUtils.calculateFieldsMetaData(approvalCase.approvedElement.entityType!, fields);
    let concernedLines: string = '';
    let employeeId = LoginUtils.currentUserEmployeeId();
    if (employeeId)
      concernedLines = ApprovalUtils.calcConcernedLines(approvalCase.currentCandidates, employeeId);
    if (concernedLines == null)
      return;
    let questionPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter(approvalCase.approvedElement?.entityType!);
    questionPresenter.searchOwnerEntityType = DomainBaseEntities.ApprovalDefinition;
    let questionFieldsWithValues: QuestionFieldsWithValues = ApprovalUtils.createApprovalQuestionFieldsWithValues(approvalCase.nextStepName1, approvalCase.concernedLines, approvalCase.nextStepSequence, fieldsMetadata, approvalCase.summary, "", questionPresenter);
    let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
    });
    await questionPresenter.edit(questionFieldsWithValues.questionFields ?? [], questionFieldsWithValues.defaultValues ?? []);
    let actions = this.addApprovalDecisionActions(approvalCase, fieldsMetadata, presenter, stepsAllowedForReturn,
      questionFieldsWithValues.questionFields ?? []);
    actions.push(cancelBtn);
    let dialogBox = new DialogHandler(actions, Translator.translateId("approval"));
    await dialogBox.createPresenter(questionPresenter as WidgetsPresenter);
    await dialogBox.runDialogBox();
  }

  private static isApprovedToDeleteEntity(response: Record<string, any>) {
    return response.initiator == "Delete" && response.state == ApprovalCaseState.Approved;
  }

  private static createApproveRequest(approvalCase: namaTypes.DTOApprovalCase, approver: string | undefined, object: Record<string, any>, fieldsMetadata: namaTypes.FieldMetaData[]): ApproveRequest {
    let approveRequest: ApproveRequest = {
      entityType: approvalCase.approvedElement?.entityType,
      entityId: approvalCase.approvedElement?.id,
      approverId: approver,
      decision: object.decision,
      comment: object.remarks,
      stepSequence: approvalCase.nextStepSequence,
      approvalReasonIdOrCode: object.approvalReason ? object.approvalReason.id : undefined,
      escalateTo: object.escalateTo,
      otp: object.otp,
      returnedToStepSequence: object.returnedToStepSeq ? Number(object.returnedToStepSeq) : undefined
    }
    if (object.attachment1) {
      approveRequest.attachment = object.attachment1 as DTOLargeData;
    }
    let fieldsValues: string[] = [];
    let fieldIds: string[] = [];
    for (let metaData of fieldsMetadata) {
      let fieldId: string | undefined = metaData.fieldId;
      if (!fieldId)
        continue;
      fieldsValues.push(object[fieldId]);
      fieldIds.push(fieldId);
    }
    approveRequest.fieldValues = fieldsValues;
    approveRequest.fieldIds = fieldIds;
    return approveRequest;
  }

  static calcConcernedLines(values: string | undefined, employeeId: string): string {
    if (!values)
      return "";
    let parts = values.split(CommonConstants.SEPARATOR);
    for (let part of parts) {
      if (part.startsWith(employeeId))
        return part.substring(employeeId.length + 1);
    }
    return ""
  }

  static calculateFieldsMetaData(entityType: string, fieldIds: string | undefined): FieldMetaData[] {
    let fieldsList: FieldMetaData[] = [];
    let uiPreferences = ClientCache.uiPreferencesCache[entityType];
    let metaData: NaMaMetaData = {};
    if (uiPreferences && uiPreferences.metaData)
      metaData = uiPreferences.metaData;
    if (!fieldIds)
      fieldIds = "";
    let parts: string[] = fieldIds.split(CommonConstants.SEPARATOR);
    for (let field1Id of parts) {
      if (field1Id) {
        let fieldMetaData: FieldMetaData | undefined = MetaData.getFieldMetadata(metaData, field1Id);
        if (fieldMetaData)
          fieldsList.push(fieldMetaData);
      }
    }
    return fieldsList;
  }

  private static async loadTakenStepsIfMissing(approvalCase: DTOApprovalCase): Promise<DTOApprovalCase> {
    if (!ApprovalUtils.isReturnToSpecificStepAllowed() || !approvalCase.id || approvalCase.steps?.length)
      return approvalCase;
    try {
      let fullApprovalCase: DTOApprovalCase = await NamaCommon.namaFetch(NamaURLS.Find_Approval_Case_By_Id,
        {params: {approvalCaseID: approvalCase.id}, doNotPushErrorWhenFailure: true, noLoadingIndicator: true});
      return fullApprovalCase ?? approvalCase;
    } catch (ignored) {
      return approvalCase;
    }
  }

  static calcStepsAllowedForReturn(approvalCase: DTOApprovalCase): DTOApprovalCaseStep[] {
    let currentStepSeq = approvalCase.nextStepSequence;
    if (!currentStepSeq)
      return [];
    let allowedSteps: DTOApprovalCaseStep[] = [];
    for (let step of approvalCase.steps ?? []) {
      if (!step.approvalStepSeq || step.approvalStepSeq >= currentStepSeq)
        continue;
      if (allowedSteps.some(allowed => allowed.approvalStepSeq === step.approvalStepSeq))
        continue;
      allowedSteps.push(step);
    }
    return allowedSteps;
  }

  private static addReturnToStepFieldAfterRemarks(questionFields: QuestionField[], allowedSteps: DTOApprovalCaseStep[]): QuestionField[] {
    let fields: QuestionField[] = [...questionFields];
    if (!ApprovalUtils.isReturnToSpecificStepAllowed() || !allowedSteps.length)
      return fields;
    let remarksIndex = fields.findIndex(field => field.id === "remarks");
    if (remarksIndex < 0)
      fields.push(ApprovalUtils.createReturnToStepField(allowedSteps));
    else
      fields.splice(remarksIndex + 1, 0, ApprovalUtils.createReturnToStepField(allowedSteps));
    return fields;
  }

  private static createReturnToStepField(allowedSteps: DTOApprovalCaseStep[]): QuestionField {
    let field: QuestionField = NamaCommon.createQuestionFieldWithAllowedValues("returnedToStepSeq", FieldType.Enum,
      allowedSteps.map(step => `${step.approvalStepSeq}`));
    field.metaData!.allowedValuesTitles = allowedSteps.map(step => ({
      id: `${step.approvalStepSeq}`,
      title: {
        arabicText: ApprovalUtils.calcStepTitle(step, step.approvalStepName1),
        englishText: ApprovalUtils.calcStepTitle(step, step.approvalStepName2)
      }
    }));
    return field;
  }

  private static calcStepTitle(step: DTOApprovalCaseStep, stepName: string | undefined): string {
    return stepName ? `${step.approvalStepSeq} - ${stepName}` : `${step.approvalStepSeq}`;
  }

  static headerApproval(text: string): boolean {
    return text === "-1";
  }

  static calcDisplayString(text: string) {
    if (!text || this.headerApproval(text))
      return "";
    let list: number[] = this.calcIndices(text);
    if (!list)
      return "";
    let ret: string = "" + list[0];
    for (let i = 1; i < list.length; i++) {
      ret += "," + list[i];
    }
    return ret;
  }

  static calcIndices(text: string): number[] {
    let integers: number[] = [];
    if (!text || this.headerApproval(text))
      return integers;
    let newText: string = "";
    for (let i = 0; i < text.length; i++) {
      if (isNaN(parseInt(text.charAt(i), 10)))
        newText += ',';
      else
        newText += text.charAt(i);
    }
    text = newText;
    let split: string[] = text.split(",");
    if (!split)
      return integers;
    for (let num of split) {
      if (!num)
        continue;
      integers.push(parseInt(num));
    }
    return integers;
  }

  static createApprovalQuestionFieldsWithValues(nextStepName: string | undefined, concernedLines: string | undefined, nextStepSeq: number | undefined, fields: FieldMetaData[], summary: string | undefined, defaultDecision: string, questionFieldsPresenter: QuestionFieldsPresenter): QuestionFieldsWithValues {
    if (!summary) {
      summary = "";
    }
    let questionFields: QuestionField[] = [];
    let defaultValues: DTOFilledQuestionField[] = [];
    if (defaultDecision)
      defaultValues.push({id: 'decision', value: defaultDecision, objValue: defaultDecision});
    if (!nextStepName)
      nextStepName = "";
    let escalateToField: QuestionField = NamaCommon.createQuestionFieldWithAllowedValues(
      "escalateTo",
      FieldType.Genericreference,
      [DomainBaseEntities.Employee, BSCEntities.SecurityProfile, BSCEntities.EmployeeGroup]
    );
    let commentField: QuestionField = NamaCommon.createQuestionField("remarks", FieldType.BigText, EditScreenFieldLayout.spanned);

    let approvalReasonField: QuestionField = NamaCommon.createQuestionFieldRef("approvalReason", BSCEntities.ApprovalReason);
    const reasonFieldWithCriteria = Object.assign(new QuestionFieldWithCriteria(), approvalReasonField);
    reasonFieldWithCriteria.setCriteriaCalculator(() => {
      let presenterFields = questionFieldsPresenter.getQuestionFields();
      let decField = presenterFields.filter(f => f.id == "decision")[0];
      let criteria = `
      ,OpenBracket,,AND;
      approvalEntity,Equal,${questionFieldsPresenter.entityType},OR;
      approvalEntity,Equal,,OR;
      ,CloseBracket,,AND;
      `;
      if(!decField)
        return criteria;
      let decision: string | undefined = decField.value;
      if (decision)
        criteria += this.decisionWithReasonId(decision) + ",Equal,true,AND;";
      return criteria;
    });
    let otpField: QuestionField = NamaCommon.createQuestionField("otp", FieldType.Text);
    let attachmentField:QuestionField = NamaCommon.createQuestionField(IdsAndConstants.attachment1, FieldType.Binary); // TODO :: attachmentFieldWidget;
    attachmentField['isApprovalContext'] = true;
    questionFields.push(NamaCommon.createQuestionField("summary", "HtmlPanel", EditScreenFieldLayout.spanned2));
    questionFields.push(NamaCommon.createQuestionField("nextStepName", "HtmlPanel", EditScreenFieldLayout.spanned));
    questionFields.push(NamaCommon.createQuestionField("nextStepSequence", "HtmlPanel", EditScreenFieldLayout.spanned));
    defaultValues.push({id: 'summary', value: summary, objValue: summary});
    defaultValues.push({id: 'nextStepName', value: nextStepName, objValue: nextStepName});
    defaultValues.push({id: 'nextStepSequence', value: nextStepSeq ? nextStepSeq + "" : "", objValue: nextStepSeq});
    if (ClientCache.getCustomGUISettings() && (ClientCache.getCustomGUISettings()?.useEscalateToSpecificEmployee || ClientCache.getCustomGUISettings()?.useEscalateToSpecificEmployee == null))
      questionFields.push(escalateToField);
    questionFields.push(otpField);
    questionFields.push(reasonFieldWithCriteria);
    questionFields.push(attachmentField);
    questionFields.push(commentField);
    if (fields) {
      for (let metaData of fields) {
        if (!metaData.fieldId)
          continue;
        let createField: QuestionField = {metaData, id: metaData.id, required: true};
        questionFields.push(createField);
      }
    }
    return {questionFields, defaultValues} as QuestionFieldsWithValues;
  }

  private static decisionWithReasonId(decision: string): string | undefined {
    switch (decision) {
      case ApprovalDecision.Approve:
      case Translator.translateId(ApprovalDecision.Approve):
        return "usedWithApproving";
      case ApprovalDecision.Reject:
      case Translator.translateId(ApprovalDecision.Reject):
        return "usedWithRejecting";
      case ApprovalDecision.Return:
      case Translator.translateId(ApprovalDecision.Return):
        return "usedWithReturning";
      case ApprovalDecision.EscalateToSpecificEmployee:
      case Translator.translateId(ApprovalDecision.EscalateToSpecificEmployee):
      case ApprovalDecision.EscalateToSupervisor:
      case Translator.translateId(ApprovalDecision.EscalateToSupervisor):
      case ApprovalDecision.EscalateToDirectSupervisor:
      case Translator.translateId(ApprovalDecision.EscalateToDirectSupervisor):
        return "usedWithEscalationToSupervisor";
      case ApprovalDecision.ReturnToPreviousStep:
      case Translator.translateId(ApprovalDecision.ReturnToPreviousStep):
        return "usedWithReturningToPreviousStep";
      case ApprovalDecision.ReturnToSpecificStep:
      case Translator.translateId(ApprovalDecision.ReturnToSpecificStep):
        return "usedWithReturningToSpecificStep";
    }
    return "usedWithApproving";
  }

  private static isReturnToSpecificStepAllowed(): boolean {
    let customGUISettings = ClientCache.getCustomGUISettings();
    if (!customGUISettings)
      return false;
    return customGUISettings.useReturnToSpecificStep || customGUISettings.useReturnToSpecificStep == null;
  }

  private static addApprovalDecisionActions(approvalCase: namaTypes.DTOApprovalCase, fieldsMetadata: namaTypes.FieldMetaData[], presenter: EditPresenter | undefined, stepsAllowedForReturn: DTOApprovalCaseStep[], questionFields: QuestionField[]): CommonDialogAction[] {
    let allowedDecisionActions: CommonDialogAction[] = [];
    let otherDecisionActions: CommonDialogAction[] = [];
    allowedDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.Approve, approvalCase, fieldsMetadata, presenter));
    let customGUISettings = ClientCache.getCustomGUISettings();
    if (customGUISettings) {
      if (customGUISettings.useRejectDecision || customGUISettings.useRejectDecision == null)
        allowedDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.Reject, approvalCase, fieldsMetadata, presenter));
      if (customGUISettings.useReturnDecision || customGUISettings.useReturnDecision == null)
        allowedDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.Return, approvalCase, fieldsMetadata, presenter));
      if (customGUISettings.useReturnToPreviousStep || customGUISettings.useReturnToPreviousStep == null)
        otherDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.ReturnToPreviousStep, approvalCase, fieldsMetadata, presenter));
      if ((customGUISettings.useReturnToSpecificStep || customGUISettings.useReturnToSpecificStep == null) && stepsAllowedForReturn.length)
        otherDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.ReturnToSpecificStep, approvalCase, fieldsMetadata, presenter));
      if (customGUISettings.useEscalateToDirectSupervisor || customGUISettings.useEscalateToDirectSupervisor == null)
        otherDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.EscalateToDirectSupervisor, approvalCase, fieldsMetadata, presenter));
      if (customGUISettings.useEscalateToSupervisor || customGUISettings.useEscalateToSupervisor == null)
        otherDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.EscalateToSupervisor, approvalCase, fieldsMetadata, presenter));
      if (customGUISettings.useEscalateToSpecificEmployee || customGUISettings.useEscalateToSpecificEmployee == null)
        otherDecisionActions.push(this.createApprovalDialogAction(ApprovalDecision.EscalateToSpecificEmployee, approvalCase, fieldsMetadata, presenter));
    }
    if (otherDecisionActions.length > 0)
      allowedDecisionActions.push(this.createOtherDecisionDialogAction(IdsAndConstants.OTHER_DECISION, otherDecisionActions,
        approvalCase.approvedElement?.entityType, questionFields, stepsAllowedForReturn));
    return allowedDecisionActions;
  }
}
